import {Injectable} from "@angular/core";
import {Patient} from "../../clinical/patient/models/patient";
import {PatientId} from "../../clinical/patient/models/patient.id";
import {LogCat, LogManager} from "../../../common/log";
import {PatientIdIssuerService} from "./patient.id.issuer.service";
import {StudyUtilsService} from "./study.utils.service";
import {SystemConfigurationValues} from "../../../common/configuration/services/system.configuration.utils";
import {StringInterpolationService} from "../../../common/string.interpolation/string.interpolation.service";
import {IPatientId} from "../../patient.summary/interfaces/interfaces";
import {MaskApplierService} from "ngx-mask";
import * as _ from "lodash";

//export IPatientUtilsStudyItem;
// need to check why we need the PatientUtilsService as a provider in SessionContainer
@Injectable({ providedIn: 'root' })
  export class PatientUtilsService {
	constructor(private patientIdIssuerService: PatientIdIssuerService,
				private studyUtils:StudyUtilsService,
				private stringInterpolationService:StringInterpolationService,
				private sysCfg:SystemConfigurationValues,
				private maskApplierService: MaskApplierService) {
	}

	public getFullNameObj(name:{firstName:string;lastName:string;middleName:string;}): string {
		let pattern=this.sysCfg.getValidatedString("PatientFullNamePattern","[FIRST_NAME] - [LAST_NAME], [MIDDLE_NAME]");
		return this.stringInterpolationService.interpolate(name,pattern,this.sysCfg.getValidatedString("PatientFullNamePatternPlaceHolder","---"));
	}
	/**
	 * Combines full patient name.
	 * @param firstName Patient first name.
	 * @param lastName Patient last name.
	 * @returns Patient full name.
	 */
	public getFullName(firstName: string, lastName: string,middleName:string=''): string {
		return this.getFullNameObj({
			lastName,firstName,middleName
		});
	}

	/**
	 * Gets the gender description.
	 * @param {Patient} patient.
	 * @returns {string} Gender description.
	 */
	public getGender(patient: Patient): string {
		return (patient.FKLookupRows && patient.FKLookupRows.genderKey && patient.FKLookupRows.genderKey.description) ?
			patient.FKLookupRows.genderKey.description : null;
	}

	/**
	 * Gets the study descriptions list
	 * @param {Patient} patient.
	 * @returns {string} Study descriptions.
	 */
	public getStudyDescriptions(patient: Patient) : string[]{
		return this.studyUtils.getAllDeepestStudiesDescriptions(patient, true);
	}

	/**
	 * Gets the gender code.
	 * @param {Patient} patient.
	 * @returns {string} Gender description.
	 */
	// public getGenderCode(patient: Patient): string {
	// 	return (patient.FKLookupRows && patient.FKLookupRows.genderKey && patient.FKLookupRows.genderKey.code) ?
	// 		patient.FKLookupRows.genderKey.code : null;
	// }

	/**
	 * Returns patient type.
	 * @param {Patient} patient.
	 * @returns {string} Patient type.
	 */
	public getPatientType(patient: Patient): string {
		let type;
		if (this.notEmptyArray(patient.visit)) {
			patient.visit.forEach(v => {
				type = !type && v.FKLookupRows && v.FKLookupRows.patientClassKey && v.FKLookupRows.patientClassKey.description ?
					v.FKLookupRows.patientClassKey.description : type;
			});
		}
		return type;
	}

	/**
	 * Gets primary patient ID.
	 * @param {Patient | PatientId | PatientId[]} value
	 * @param {boolean} displayIssuer
	 * @param {boolean} displayDefaultIssuer
	 * @param {boolean} implementMask
	 * @returns {string}
	 */
	public getPrimaryId(value: Patient|PatientId|PatientId[],
						displayIssuer: boolean,
						displayDefaultIssuer: boolean = false,
						implementMask: boolean = false): string {
		let row = {patientId : ''};

		if (value instanceof Patient) {
			value = value.patientIdList;
		}

		if (Array.isArray(value)) {
			if (!value.length) {
				return '';
			}

			row = value.find((id) => id.primary === "Y");

			if (!row) {
				LogManager.warn(LogCat.Clinical.Patient.Id, "no primary patient id");
				row = value[0];
			}
		} else {
			row = value;
		}

		if (implementMask) {
			row = _.clone(row);
			row.patientId = this.getPatientIdMask() ? this.applyMask(row.patientId, this.getPatientIdMask()) : row.patientId;
		}

		if (displayIssuer) {
			let curIssuer = row && row["issuerOfPid"] ? row["issuerOfPid"] :
				row && row["FKLookupRows"] && row["FKLookupRows"].issuerOfPidKey ? row["FKLookupRows"].issuerOfPidKey.displayName  : "";
			return curIssuer && (!this.patientIdIssuerService.isDefaultPatientIdIssuer(curIssuer) || displayDefaultIssuer) ?
				`${row ? row.patientId + " (" + curIssuer + ")" : ""}` : `${row ? row.patientId : ""}`;
		}

		return `${row ? row.patientId : ""}`;
	}

	/**
	 * Implements patient ID mask
	 * @param id
	 * @param mask
	 */
	public applyMask(id: string, mask: string) {
		return this.maskApplierService.applyMask(id, mask);
	}

	/**
	 * Gets primary patient ID issuer.
	 * @param idList - array of patient ids objects.
	 * @returns Primary patient ID issuer.
	 */
	public getPrimaryIdIssuer(idList: PatientId[],showFirstIfNoPrimFound=false): string {
		let primaryPatientId = this.getPrimaryPatientId(idList,showFirstIfNoPrimFound);
		if (primaryPatientId && primaryPatientId["FKLookupRows"] && primaryPatientId["FKLookupRows"].issuerOfPidKey) {
			return primaryPatientId["FKLookupRows"].issuerOfPidKey.displayName;
		} else if (primaryPatientId && primaryPatientId.issuerOfPidKey) {
			return this.patientIdIssuerService.getIssuerByIssuerKey(primaryPatientId.issuerOfPidKey);
		}
		return null;
	}

	/**
	 * Gets primary patient ID issuer key.
	 * @param idList - array of patient ids objects.
	 * @returns Primary patient ID issuer key.
	 */
	public getPrimaryIdIssuerKey(idList: PatientId[]): number {
		let primaryPatientId = this.getPrimaryPatientId(idList);
		if (primaryPatientId) {
			return primaryPatientId.issuerOfPidKey;
		}
	}

	/**
	 * Gets primary patient ID object.
	 * @param idList - array of patient ids objects.
	 * @returns Primary patient ID object.
	 */
	public getPrimaryPatientId(idList: PatientId[],showFirstIfNotFound=false): PatientId {
		if (idList && idList.length) {
			let result = idList.find((id) => id.primary === 'Y');
			if (!result && showFirstIfNotFound){
				result=idList[0];
			}
			return result;
		} else {
			return null;
		}
	}

	/**
	 * Gets the list of all actual and previous patient ids
	 * @param p - current patient
	 */
	public getPatientIds(p: Patient): IPatientId[] {
		let res: IPatientId[] = [];

		if (p && p.patientIdList && p.patientIdList.length) {
			p.patientIdList.forEach(i => {
				res.push({
					id: (this.implementMask(i.issuerOfPidKey) && this.getPatientIdMask()) ?
						this.applyMask(i.patientId, this.getPatientIdMask()) : i.patientId,
					issuer: i["FKLookupRows"] && i["FKLookupRows"].issuerOfPidKey && i["FKLookupRows"].issuerOfPidKey.displayName ?
						i["FKLookupRows"].issuerOfPidKey.displayName : '',
					sequence: i.sequenceId,
					primary: i.primary.toUpperCase() === 'Y',
					previous: false
				});
			});
		}

		if (p && p.patientPreviousId && p.patientPreviousId.length) {
			p.patientPreviousId.forEach(i => {
				res.push({
					id: (this.implementMask(i.issuerOfPidKey) && this.getPatientIdMask()) ?
						this.applyMask(i.patientId, this.getPatientIdMask()) : i.patientId,
					issuer: i["FKLookupRows"] && i["FKLookupRows"].issuerOfPidKey && i["FKLookupRows"].issuerOfPidKey.displayName ?
						i["FKLookupRows"].issuerOfPidKey.displayName : '',
					sequence: i.sequenceId,
					primary: false,
					previous: true
				});
			});
		}

		return res;
	}

	/**
	 * Gets primary patient address.
	 * @param addressList - array of patient address objects.
	 * @returns Primary patient address.
	 */
	public getPrimaryAddress(addressList: any[], emptyValue = {}): any {
		if (addressList) {
			let result = addressList.length===1?addressList[0]:addressList.find((address) => address.primary === 'Y');
			return result ? result : emptyValue;
		}
	}

	/**
	 * Returns the mask format for patientId. If web specified mask is empty, use the regular RIS mask.
	 */
	public getPatientIdMask(): string {
		return this.sysCfg.getValidatedString('IDMaskFormatWeb') || this.sysCfg.getValidatedString('IDMaskFormat');
	}

	/**
	 * Checks if patient ID mask shall be implemented for current issuer.
	 * @param assigningAuthorityKey
	 */
	public implementMask(assigningAuthorityKey: number): boolean {
		if (assigningAuthorityKey) {
			let implementMaskFor: number[] = this.sysCfg.getNumberArray('AssigningAuthorityKeysForIDMasking');
			return implementMaskFor.length ? implementMaskFor.includes(assigningAuthorityKey) : true;
		}
		return false;
	}

	/**
	 * Checks if a current array is empty or contains elements
	 * @param array to check
	 * @returns true if contains
	 */
	protected notEmptyArray(arr: Array<any>) {
		return arr && arr.length;
	}
}
